import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./topstudents.css";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";
import { useCatalogue } from "../../../hooks/useCatalogue";

const MEDALS = ["🥇", "🥈", "🥉"];

function TopStudents() {
  const [sessions, setSessions] = useState([]);
  const [current_session, setCurrent_session] = useState("");
  const [classes, setClasses] = useState([]);
  const [current_class, setCurrent_class] = useState(null); // { _id, level }
  const [current_department, setCurrent_department] = useState(null); // { _id, name } | null
  const [semester, setSemester] = useState(null); // 1 | 2 | null
  const [students, setStudents] = useState([]);
  const [scoreKey, setScoreKey] = useState("cgpa");
  const [topLimit, setTopLimit] = useState(10);
  const [load, setLoad] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  const { departments: deptList } = useCatalogue();

  const departments = useMemo(
    () => deptList.map((d) => ({ _id: d._id, name: d.name })),
    [deptList]
  );

  // ─── fetch ────────────────────────────────────────────────────────────────

  const fetchStudents = useCallback(
    async ({ classObj, deptObj, sem, limit }) => {
      if (!classObj?._id) return;
      setLoad(true);
      try {
        const semParam = sem ? `&semester=${sem}` : "";
        let res, json;
        if (deptObj) {
          res = await authFetch(
            `${API_BASE_URL}/api/class/topstudents/department/${classObj._id}?limit=${limit}${semParam}`,
            {
              method: "POST",
              body: JSON.stringify({ department_id: deptObj._id }),
            }
          );
        } else {
          res = await authFetch(
            `${API_BASE_URL}/api/class/topstudents/${classObj._id}?limit=${limit}${semParam}`
          );
        }
        json = await res.json();
        setStudents(json.topStudents || []);
        setScoreKey(json.scoreKey || (deptObj ? "department_gpa" : "cgpa"));
      } catch (err) {
        console.error(err);
      } finally {
        setLoad(false);
      }
    },
    []
  );

  const fetchSessions = useCallback(async () => {
    setLoad(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/sessions/`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed to load sessions");
      setSessions(json.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ─── handlers ─────────────────────────────────────────────────────────────

  const handleSession = (session) => {
    setCurrent_session(session.session);
    setClasses(session.classes || []);
    setCurrent_class(null);
    setCurrent_department(null);
    setSemester(null);
    setStudents([]);
  };

  const handleClass = (level) => {
    const classObj = { _id: level._id, level: level.level };
    setCurrent_class(classObj);
    setCurrent_department(null);
    setSemester(null);
    setTopLimit(10);
    fetchStudents({ classObj, deptObj: null, sem: null, limit: 10 });
  };

  const handleDepartment = (dept) => {
    const next = current_department?._id === dept._id ? null : dept;
    setCurrent_department(next);
    setTopLimit(10);
    fetchStudents({ classObj: current_class, deptObj: next, sem: semester, limit: 10 });
  };

  const handleSemester = (sem) => {
    const next = semester === sem ? null : sem;
    setSemester(next);
    setTopLimit(10);
    fetchStudents({ classObj: current_class, deptObj: current_department, sem: next, limit: 10 });
  };

  const handleViewMore = () => {
    const next = topLimit + 10;
    setTopLimit(next);
    fetchStudents({ classObj: current_class, deptObj: current_department, sem: semester, limit: next });
  };

  // ─── print ────────────────────────────────────────────────────────────────

  const handlePrint = () => {
    const content = printRef.current?.innerHTML || "";
    const win = window.open("", "_blank");
    const semLabel = semester === 1 ? " — Semester 1" : semester === 2 ? " — Semester 2" : "";
    const deptLabel = current_department ? ` · ${current_department.name}` : "";
    win.document.write(`
      <html>
        <head>
          <title>Top Students — ${current_class?.level} Level, ${current_session}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; padding: 2rem; color: #0f172a; }
            .print_header { text-align: center; margin-bottom: 1.5rem; border-bottom: 2px solid #0f172a; padding-bottom: 1rem; }
            .print_header h1 { font-size: 1rem; text-transform: uppercase; letter-spacing: 0.08em; }
            .print_header h2 { font-size: 0.85rem; font-weight: normal; margin-top: 0.25rem; color: #475569; }
            .print_meta { font-size: 0.8rem; color: #64748b; margin-top: 0.5rem; }
            table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
            th, td { padding: 8px 12px; border: 1px solid #cbd5e1; font-size: 0.85rem; text-align: left; }
            th { background: #0f172a; color: white; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.06em; }
            tr:nth-child(even) td { background: #f8fafc; }
            .rank { text-align: center; font-weight: bold; }
            .print_footer { margin-top: 2rem; font-size: 0.75rem; color: #94a3b8; text-align: right; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  // ─── CSV export ───────────────────────────────────────────────────────────

  const handleExportCSV = () => {
    const scoreLabel = scoreKey === "cgpa" ? "CGPA" : scoreKey === "department_gpa" ? "Dept GPA" : scoreKey === "sem1_gpa" ? "Sem 1 GPA" : "Sem 2 GPA";
    const rows = [
      ["Rank", "Reg No", "Full Name", scoreLabel],
      ...students.map((s, i) => [i + 1, s.reg_no, s.fullname, (s[scoreKey] ?? s.cgpa ?? 0).toFixed(2)]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const semPart = semester ? `_sem${semester}` : "";
    const deptPart = current_department ? `_${current_department.name.replace(/\s+/g, "_")}` : "";
    a.download = `top_students_${current_class?.level}L_${current_session}${semPart}${deptPart}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── score display ────────────────────────────────────────────────────────

  const scoreLabel = scoreKey === "cgpa" ? "CGPA" : scoreKey === "department_gpa" ? "Dept GPA" : scoreKey === "sem1_gpa" ? "Sem 1 GPA" : "Sem 2 GPA";

  const getScore = (s) => {
    const v = s[scoreKey] ?? s.cgpa ?? 0;
    return Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "--";
  };

  // ─── print content ────────────────────────────────────────────────────────

  const semLabel = semester === 1 ? " — Semester 1" : semester === 2 ? " — Semester 2" : "";
  const deptLabel = current_department ? ` · ${current_department.name}` : "";
  const printTitle = current_class
    ? `Top ${students.length} Students — ${current_class.level} Level, ${current_session}${semLabel}${deptLabel}`
    : "";

  if (error) {
    return (
      <div className="top_students">
        <div className="ts_error">
          <p>Something went wrong: {error}</p>
          <button onClick={fetchSessions}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="top_students">
      {/* ── toolbar ── */}
      <div className="ts_toolbar no_print">
        <div className="ts_panel">
          <span className="ts_panel_label">Session</span>
          <div className="ts_chips">
            {sessions.map((s) => (
              <button
                key={s.session}
                className={`ts_chip${current_session === s.session ? " active" : ""}`}
                onClick={() => handleSession(s)}
              >
                {s.session}
              </button>
            ))}
          </div>
        </div>

        {classes.length > 0 && (
          <div className="ts_panel">
            <span className="ts_panel_label">Level</span>
            <div className="ts_chips">
              {classes.map((lvl) => (
                <button
                  key={lvl._id}
                  className={`ts_chip${current_class?._id === lvl._id ? " active" : ""}`}
                  onClick={() => handleClass(lvl)}
                >
                  {lvl.level} Level
                </button>
              ))}
            </div>
          </div>
        )}

        {current_class && (
          <div className="ts_panel">
            <span className="ts_panel_label">Semester</span>
            <div className="ts_chips">
              {[1, 2].map((sem) => (
                <button
                  key={sem}
                  className={`ts_chip${semester === sem ? " active" : ""}`}
                  onClick={() => handleSemester(sem)}
                >
                  {sem === 1 ? "Semester 1" : "Semester 2"}
                </button>
              ))}
            </div>
          </div>
        )}

        {current_class && departments.length > 0 && (
          <div className="ts_panel ts_panel_full">
            <span className="ts_panel_label">Department <span className="ts_panel_hint">(click to filter, click again to clear)</span></span>
            <div className="ts_chips">
              {departments.map((dept) => (
                <button
                  key={dept._id}
                  className={`ts_chip${current_department?._id === dept._id ? " active" : ""}`}
                  onClick={() => handleDepartment(dept)}
                >
                  {dept.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── results ── */}
      {!current_class && !load && (
        <div className="ts_empty">Select a session and level to view top students.</div>
      )}

      {current_class && (
        <div className="ts_results">
          <div className="ts_results_header no_print">
            <h2 className="ts_title">{printTitle || "Top Students"}</h2>
            {students.length > 0 && (
              <div className="ts_actions">
                <button className="ts_btn ts_btn_csv" onClick={handleExportCSV}>Export CSV</button>
                <button className="ts_btn ts_btn_print" onClick={handlePrint}>Print</button>
              </div>
            )}
          </div>

          {/* print-only header lives inside printRef */}
          <div ref={printRef}>
            <div className="print_header">
              <h1>Faculty of Pharmaceutical Sciences — University of Nigeria, Nsukka</h1>
              <h2>{printTitle}</h2>
              <p className="print_meta">Generated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>

            {students.length > 0 && (
              <table className="ts_table">
                <thead>
                  <tr>
                    <th className="rank">Rank</th>
                    <th>Reg No</th>
                    <th>Full Name</th>
                    <th>{scoreLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => (
                    <tr key={s.reg_no}>
                      <td className="rank">{MEDALS[i] || i + 1}</td>
                      <td>{s.reg_no}</td>
                      <td>{s.fullname}</td>
                      <td className="score">{getScore(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!load && students.length === 0 && (
              <div className="ts_empty">No students found for this selection.</div>
            )}
          </div>

          {students.length > 0 && students.length === topLimit && (
            <div className="ts_more no_print">
              <button className="ts_btn ts_btn_more" onClick={handleViewMore}>
                View 10 More
              </button>
            </div>
          )}
        </div>
      )}

      {load && <Loader />}
    </div>
  );
}

export default TopStudents;
